import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import { motion } from 'framer-motion';
import { CheckCircle2, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import i18n from '../../i18n';
import { darkTheme } from '../../themes';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { checkBackendNow } from '../../utils/backendStatus';
import useBackendStatus from '../../hooks/useBackendStatus';

import RMF_LOGO from '../../assets/images/fullLogo.png';
import RightFade from '../../assets/images/firstBuyFunded/RightFade.png';
import LeftStar from '../../assets/images/firstBuyFunded/LeftStar.png';
import enFlag from '../../assets/icons/flags/en.png';
import frFlag from '../../assets/icons/flags/fr.png';
import esFlag from '../../assets/icons/flags/es.png';
import arFlag from '../../assets/icons/flags/sa.png';

import {
  bgImgSx,
  cardSx,
  contentSx,
  iconWrapSuccessSx,
  iconWrapSx,
  langButtonSx,
  logoSx,
  pageRootSx,
  progressSx,
  retryButtonSx,
  statusLineSx,
  subtitleSx,
  supportSx,
  titleSx,
} from './styles';

const ease = [0.25, 0.1, 0.25, 1] as [number, number, number, number];

const SUPPORT_EMAIL = 'support@raisemyfunds.co';

/**
 * Delay before reloading once the API answers again. Long enough to read the "back online" state
 * and to reach the button for whoever does not want to wait, short enough not to feel stuck.
 */
const RELOAD_DELAY_MS = 4_000;

const LOCALES: { code: string; label: string; flag: string }[] = [
  { code: 'en', label: 'English', flag: enFlag },
  { code: 'fr', label: 'Français', flag: frFlag },
  { code: 'es', label: 'Español', flag: esFlag },
  { code: 'ar', label: 'العربية', flag: arFlag },
];

interface MaintenancePageProps {
  /** Renders the outage state even when the API answers, for design review and QA. */
  preview?: boolean;
}

/**
 * Full-screen screen shown while the gateway API is unreachable, typically during a deploy.
 *
 * It is entirely self-contained: no provider, no API call other than the liveness probe, and its
 * assets are bundled, so it renders even when every backend call fails. Once the API answers
 * again the page reloads itself, which is also what a post-deploy client needs.
 */
const MaintenancePage: React.FC<MaintenancePageProps> = ({ preview = false }) => {
  const { t } = useTranslation('maintenance');
  const status = useBackendStatus();
  const [checking, setChecking] = useState(false);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);

  const isRtl = i18n.language?.startsWith('ar') ?? false;
  const recovered = !preview && status === 'up';

  useDocumentTitle(t('DOCUMENT_TITLE'));

  useEffect(() => {
    if (!recovered) return undefined;

    const timer = setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [recovered]);

  const handleRetry = async () => {
    setChecking(true);
    try {
      await checkBackendNow();
    } finally {
      setChecking(false);
    }
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    setLangAnchor(null);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box sx={pageRootSx} dir={isRtl ? 'rtl' : 'ltr'}>
        <Box component="img" src={RightFade} alt="" sx={bgImgSx.rightFade} />
        <Box component="img" src={LeftStar} alt="" sx={bgImgSx.leftStar} />

        <Box sx={contentSx}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
          >
            <Box component="img" src={RMF_LOGO} alt="RaiseMyFunds" sx={logoSx} />
          </motion.div>

          <Box sx={cardSx} component={motion.div} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <Box sx={recovered ? iconWrapSuccessSx : iconWrapSx}>
              {recovered ? (
                <CheckCircle2 size={34} />
              ) : (
                <motion.div
                  animate={{ rotate: [0, 14, -8, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ display: 'flex' }}
                >
                  <Wrench size={32} />
                </motion.div>
              )}
            </Box>

            <Stack spacing={1.25} alignItems="center">
              <Typography sx={titleSx}>
                {recovered ? t('RESTORED_TITLE') : t('TITLE')}
              </Typography>
              <Typography sx={subtitleSx}>
                {recovered ? t('RESTORED_TEXT') : t('TEXT')}
              </Typography>
            </Stack>

            {recovered && (
              <Button sx={retryButtonSx} onClick={() => window.location.reload()}>
                {t('RESTORED_CTA')}
              </Button>
            )}

            {!recovered && (
              <>
                <LinearProgress sx={progressSx} />
                <Typography sx={statusLineSx}>
                  {checking && <CircularProgress size={13} thickness={5} color="inherit" />}
                  {checking ? t('CHECKING') : t('AUTO_RETRY')}
                </Typography>
                <Button sx={retryButtonSx} onClick={handleRetry} disabled={checking}>
                  {t('RETRY')}
                </Button>
              </>
            )}

            <Typography sx={supportSx}>
              {t('SUPPORT')} <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={(event) => setLangAnchor(event.currentTarget)}
          sx={langButtonSx}
          aria-label="language"
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langAnchor}
          open={Boolean(langAnchor)}
          onClose={() => setLangAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          {LOCALES.map((locale) => (
            <MenuItem key={locale.code} onClick={() => handleLanguageChange(locale.code)}>
              <Box
                component="img"
                src={locale.flag}
                alt={locale.label}
                sx={{ height: '18px', width: '24px', borderRadius: '2px', marginRight: '8px' }}
              />
              <ListItemText>{locale.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </ThemeProvider>
  );
};

export default MaintenancePage;
